import { useCallback, useRef, useState } from 'react'
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Handle,
  Position,
  useReactFlow,
  Panel,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import Dagre from '@dagrejs/dagre'
import {
  ChevronDown, ChevronRight, Play, Loader2, CheckCircle, XCircle, Pause,
  Upload, GitBranch, Server, FileCode, FileSymlink, Database,
  BarChart3, GitFork, Trash2, Scale, Workflow, BookOpen,
  Wand2, TextCursor, SplitSquareVertical, Sparkles,
  Code2, TestTube2, FileText, MessageSquare, Lightbulb, CheckSquare,
  Archive, GitPullRequest, FileSpreadsheet, UserCheck, Download,
  Clock, Bell, Repeat, GitMerge, Save, Settings2,
  AlignHorizontalSpaceAround, AlignVerticalSpaceAround
} from 'lucide-react'
import { NODE_REGISTRY, generateTemplateNodes, isTemplateNode } from '../../data/nodeRegistry'
import { POC_LOCKED } from '../../constants/pocConfig'
// Tooltip component kept for toolbar buttons (DesignerView.jsx), nodes use native title attr
import { NODE_TOOLTIPS, NODE_STATE_TOOLTIPS } from '../../constants/tooltipContent'
import './WorkflowCanvas.css'

// Auto-layout using Dagre
const getLayoutedElements = (nodes, edges, direction = 'LR') => {
  const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}))

  g.setGraph({
    rankdir: direction,
    nodesep: 80,      // Horizontal spacing between nodes
    ranksep: 120,     // Vertical spacing between ranks
    marginx: 50,
    marginy: 50,
  })

  nodes.forEach((node) => {
    g.setNode(node.id, {
      width: 220,   // Approximate node width
      height: 80,   // Approximate node height
    })
  })

  edges.forEach((edge) => {
    g.setEdge(edge.source, edge.target)
  })

  Dagre.layout(g)

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = g.node(node.id)
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - 110,  // Center the node (width/2)
        y: nodeWithPosition.y - 40,   // Center the node (height/2)
      },
    }
  })

  return { nodes: layoutedNodes, edges }
}

// Icon mapping
const ICONS = {
  Upload, GitBranch, Server, FileCode, FileSymlink, Database,
  BarChart3, GitFork, Trash2, Scale, Workflow, BookOpen,
  Wand2, TextCursor, SplitSquareVertical, Sparkles,
  Code2, TestTube2, FileText, MessageSquare, Lightbulb, CheckSquare,
  Archive, GitPullRequest, FileSpreadsheet, UserCheck, Download,
  Clock, Bell, Repeat, GitMerge, Save, Settings2
}

// Generate simple tooltip content for workflow nodes
function getSimpleNodeTooltip(data, nodeDef) {
  const nodeTooltip = NODE_TOOLTIPS[data.nodeType]
  const baseTooltip = nodeTooltip?.simple || nodeDef.description || 'Workflow node'

  // Add status if available
  if (data.status) {
    const stateTooltip = NODE_STATE_TOOLTIPS[data.status]
    const statusText = stateTooltip?.simple || data.status
    return `${baseTooltip}\n\nStatus: ${statusText}`
  }

  return baseTooltip
}

// Generate advanced tooltip content for workflow nodes
function getAdvancedNodeTooltip(data, nodeDef) {
  const nodeTooltip = NODE_TOOLTIPS[data.nodeType]
  let tooltip = nodeTooltip?.advanced || nodeDef.description || 'Workflow node'

  // Add status details
  if (data.status) {
    const stateTooltip = NODE_STATE_TOOLTIPS[data.status]
    tooltip += `\n\nStatus: ${stateTooltip?.advanced || data.status}`
  }

  // Add error if present
  if (data.error) {
    tooltip += `\n\nError: ${data.error}`
  }

  // Add job IDs if available
  if (data.executionResult?.jobs) {
    const jobs = data.executionResult.jobs
    const jobIds = Object.entries(jobs)
      .filter(([_, v]) => v)
      .map(([k, v]) => `${k}: ${v}`)
    if (jobIds.length > 0) {
      tooltip += `\n\nJob IDs:\n${jobIds.join('\n')}`
    }
  }

  // Add source hash if available
  if (data.executionResult?.source_hash) {
    tooltip += `\n\nSource Hash: ${data.executionResult.source_hash}`
  }

  // Add config summary
  if (data.config && Object.keys(data.config).length > 0) {
    const configSummary = Object.entries(data.config)
      .slice(0, 3)
      .map(([k, v]) => {
        // Skip complex objects, files, and job IDs
        if (typeof v === 'object' || k.includes('job_id') || k === 'file') return null
        return `${k}: ${v}`
      })
      .filter(Boolean)
    if (configSummary.length > 0) {
      tooltip += `\n\nConfiguration:\n${configSummary.join('\n')}`
    }
  }

  return tooltip
}

// Custom expandable node component
function WorkflowNode({ id, data, selected }) {
  const [expanded, setExpanded] = useState(false)
  const [resultsExpanded, setResultsExpanded] = useState(false)
  const nodeDef = NODE_REGISTRY[data.nodeType] || {}
  const Icon = ICONS[nodeDef.icon] || FileCode

  // Check if this node has viewable results
  const hasResults = data.status === 'complete' && data.executionResult
  const isAINode = data.nodeType?.startsWith('ai-')

  // Debug logging for AI nodes
  if (isAINode) {
    console.log(`[WorkflowNode ${data.nodeType}] status=${data.status}, hasResults=${hasResults}`, {
      executionResult: data.executionResult,
      hasDocumentation: !!data.executionResult?.documentation,
      hasOutput: !!data.executionResult?.output
    })
  }

  const getStatusIcon = () => {
    switch (data.status) {
      case 'running':
        return <Loader2 size={14} className="status-icon spinning" />
      case 'complete':
        return <CheckCircle size={14} className="status-icon complete" />
      case 'error':
        return <XCircle size={14} className="status-icon error" />
      case 'paused':
        return <Pause size={14} className="status-icon paused" />
      default:
        return null
    }
  }

  const getStatusText = () => {
    switch (data.status) {
      case 'running': return 'Running...'
      case 'complete': return 'Complete'
      case 'error': return data.error || 'Error'
      case 'paused': return 'Awaiting approval'
      default: return nodeDef.description || ''
    }
  }

  // Get primary config value to show in collapsed state
  const getPrimaryConfigValue = () => {
    if (!nodeDef.configSchema || nodeDef.configSchema.length === 0) return null
    const primaryField = nodeDef.configSchema[0]
    const value = data.config?.[primaryField.key] ?? primaryField.default

    // Format the value for display
    if (primaryField.type === 'select') {
      const option = primaryField.options?.find(o => o.value === value)
      return option?.label || value
    }
    return value
  }

  // Get formatted results for display
  const getResultsContent = () => {
    if (!data.executionResult) return null
    const result = data.executionResult

    // AI Explain - show documentation (check both top-level and output.documentation)
    const documentation = result.documentation || result.output?.documentation
    if (documentation) {
      return {
        type: 'markdown',
        title: 'Documentation',
        content: documentation
      }
    }

    // AI Review - show findings (check both top-level and output.review)
    const review = result.review || result.output?.review
    if (review) {
      return {
        type: 'review',
        title: `Code Review (${review.findings?.length || 0} findings)`,
        content: review.summary || 'Review complete',
        findings: review.findings || []
      }
    }

    // AI Transform - show code (check both top-level and output.transformed_code)
    const transformedCode = result.transformed_code || result.output?.transformed_code
    if (transformedCode) {
      return {
        type: 'code',
        title: 'Transformed Code',
        content: transformedCode,
        lines: transformedCode.split('\n').length
      }
    }

    // AI Chat - show response
    if (result.response) {
      return {
        type: 'text',
        title: 'AI Response',
        content: result.response
      }
    }

    // Generic results - show summary
    if (result.summary) {
      return {
        type: 'json',
        title: 'Results Summary',
        content: JSON.stringify(result.summary, null, 2)
      }
    }

    // Job info
    if (result.jobs) {
      const jobKeys = Object.keys(result.jobs).filter(k => result.jobs[k])
      if (jobKeys.length > 0) {
        return {
          type: 'info',
          title: 'Completed',
          content: `Job: ${result.jobs[jobKeys[0]]}`
        }
      }
    }

    return null
  }

  // Generate tooltip content
  const simpleTooltip = getSimpleNodeTooltip(data, nodeDef)

  return (
      <div
        className={`workflow-node ${selected ? 'selected' : ''} ${data.status || ''} ${!data.enabled ? 'disabled' : ''}`}
        style={{ '--node-color': nodeDef.color }}
        title={simpleTooltip}
      >
        {/* Input handle */}
        {nodeDef.inputs?.length > 0 && (
          <Handle
            type="target"
            position={Position.Left}
            className="node-handle input"
          />
        )}

        {/* Header */}
        <div className="node-header">
          <span className="node-icon">
            <Icon size={16} />
          </span>
          <span className="node-label">{data.label || nodeDef.label}</span>
          {getStatusIcon()}
          <button
            className="node-expand-btn"
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded) }}
          >
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        </div>

        {/* Collapsed summary */}
        {!expanded && (
          <div className="node-summary">
            {data.status ? getStatusText() : getPrimaryConfigValue()}
          </div>
        )}

        {/* Expanded config */}
        {expanded && nodeDef.configSchema && (
          <div className="node-config-preview">
            {nodeDef.configSchema.slice(0, 3).map(field => {
              const value = data.config?.[field.key] ?? field.default
              let displayValue = value

              if (field.type === 'select') {
                const option = field.options?.find(o => o.value === value)
                displayValue = option?.label || value
              } else if (field.type === 'boolean') {
                displayValue = value ? 'Yes' : 'No'
              }

              return (
                <div key={field.key} className="config-preview-row">
                  <span className="config-label">{field.label}</span>
                  <span className="config-value">{displayValue}</span>
                </div>
              )
            })}
            {nodeDef.configSchema.length > 3 && (
              <div className="config-more">+{nodeDef.configSchema.length - 3} more...</div>
            )}
          </div>
        )}

        {/* Results section - shown when node has completed with results */}
        {hasResults && (() => {
          const resultsContent = getResultsContent()
          if (!resultsContent) return null

          return (
            <div className="node-results-section">
              <button
                className="node-results-toggle"
                onClick={(e) => { e.stopPropagation(); setResultsExpanded(!resultsExpanded) }}
              >
                <span className="results-icon">
                  {resultsExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                </span>
                <span className="results-title">{resultsContent.title}</span>
              </button>

              {resultsExpanded && (
                <div className="node-results-content">
                  {resultsContent.type === 'markdown' && (
                    <div className="results-markdown">
                      <pre>{resultsContent.content}</pre>
                    </div>
                  )}
                  {resultsContent.type === 'code' && (
                    <div className="results-code">
                      <div className="results-code-header">{resultsContent.lines} lines</div>
                      <pre>{resultsContent.content}</pre>
                    </div>
                  )}
                  {resultsContent.type === 'review' && (
                    <div className="results-review">
                      <div className="results-summary">{resultsContent.content}</div>
                      {resultsContent.findings.length > 0 && (
                        <div className="results-findings">
                          {resultsContent.findings.slice(0, 5).map((f, i) => (
                            <div key={i} className={`finding-item ${f.severity}`}>
                              <span className="finding-severity">{f.severity}</span>
                              <span className="finding-issue">{f.issue}</span>
                            </div>
                          ))}
                          {resultsContent.findings.length > 5 && (
                            <div className="findings-more">+{resultsContent.findings.length - 5} more...</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {resultsContent.type === 'text' && (
                    <div className="results-text">
                      <pre>{resultsContent.content}</pre>
                    </div>
                  )}
                  {resultsContent.type === 'json' && (
                    <div className="results-json">
                      <pre>{resultsContent.content}</pre>
                    </div>
                  )}
                  {resultsContent.type === 'info' && (
                    <div className="results-info">{resultsContent.content}</div>
                  )}
                </div>
              )}
            </div>
          )
        })()}

        {/* Output handle */}
        {nodeDef.outputs?.length > 0 && (
          <Handle
            type="source"
            position={Position.Right}
            className="node-handle output"
          />
        )}
      </div>
  )
}

const nodeTypes = { workflowNode: WorkflowNode }

function DesignerWorkflowCanvas({ nodes, edges, onNodesChange, onEdgesChange, onNodeSelect, onNodeAdd, onLayout, selectedNodeId, isExecuting = false }) {
  const reactFlowWrapper = useRef(null)
  const [reactFlowInstance, setReactFlowInstance] = useState(null)

  // Auto-layout handler
  const handleLayout = useCallback((direction) => {
    if (nodes.length === 0) return

    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(nodes, edges, direction)

    // Call parent handler to update nodes
    if (onLayout) {
      onLayout(layoutedNodes, layoutedEdges)
    }

    // Fit view after layout
    setTimeout(() => {
      reactFlowInstance?.fitView({ padding: 0.2 })
    }, 50)
  }, [nodes, edges, onLayout, reactFlowInstance])

  const onConnect = useCallback(
    (params) => {
      onEdgesChange([{
        type: 'add',
        item: {
          ...params,
          id: `e-${params.source}-${params.target}`,
          type: 'smoothstep',
          animated: false,
        }
      }])
    },
    [onEdgesChange]
  )

  const onNodeClick = useCallback((event, node) => {
    onNodeSelect(node.id)
  }, [onNodeSelect])

  const onPaneClick = useCallback(() => {
    onNodeSelect(null)
  }, [onNodeSelect])

  // Handle drag over
  const onDragOver = useCallback((event) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }, [])

  // Handle drop from palette
  const onDrop = useCallback(
    (event) => {
      event.preventDefault()

      const nodeType = event.dataTransfer.getData('application/reactflow')
      if (!nodeType || !NODE_REGISTRY[nodeType]) return

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      })

      // Check if this is a template node
      if (isTemplateNode(nodeType)) {
        const { nodes: templateNodes, edges: templateEdges } = generateTemplateNodes(
          nodeType,
          position.x,
          position.y
        )

        // Add all template nodes
        templateNodes.forEach(node => onNodeAdd(node))

        // Add all template edges
        if (templateEdges.length > 0) {
          onEdgesChange(templateEdges.map(edge => ({
            type: 'add',
            item: edge
          })))
        }
      } else {
        // Regular node - existing logic
        const nodeDef = NODE_REGISTRY[nodeType]
        const newNode = {
          id: `${nodeType}-${Date.now()}`,
          type: 'workflowNode',
          position,
          data: {
            nodeType,
            label: nodeDef.label,
            config: { ...nodeDef.defaultConfig },
            enabled: true,
            status: null,
          },
        }

        onNodeAdd(newNode)
      }
    },
    [reactFlowInstance, onNodeAdd, onEdgesChange]
  )

  return (
    <div className="designer-canvas" ref={reactFlowWrapper}>
      <ReactFlow
        nodes={nodes.map(n => ({ ...n, selected: n.id === selectedNodeId }))}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        onInit={setReactFlowInstance}
        onDragOver={onDragOver}
        onDrop={onDrop}
        nodeTypes={nodeTypes}
        nodesDraggable={!isExecuting}
        nodesConnectable={!isExecuting}
        elementsSelectable={!isExecuting}
        fitView
        snapToGrid
        snapGrid={[20, 20]}
        defaultEdgeOptions={{
          type: 'smoothstep',
          style: { stroke: 'var(--border-color)', strokeWidth: 2 },
        }}
        connectionLineStyle={{ stroke: 'var(--accent-blue)', strokeWidth: 2 }}
        deleteKeyCode={POC_LOCKED ? null : ['Backspace', 'Delete']}
        proOptions={{ hideAttribution: true }}
      >
        <Controls className="flow-controls" />
        <Panel position="top-right" className="layout-panel">
          <button
            className="layout-btn"
            onClick={() => handleLayout('LR')}
            title="Auto-align horizontal (left to right)"
            disabled={nodes.length === 0}
          >
            <AlignHorizontalSpaceAround size={16} />
            <span>Horizontal</span>
          </button>
          <button
            className="layout-btn"
            onClick={() => handleLayout('TB')}
            title="Auto-align vertical (top to bottom)"
            disabled={nodes.length === 0}
          >
            <AlignVerticalSpaceAround size={16} />
            <span>Vertical</span>
          </button>
        </Panel>
        <MiniMap
          className="flow-minimap"
          nodeColor={(node) => {
            const nodeDef = NODE_REGISTRY[node.data?.nodeType]
            if (node.data?.status === 'error') return 'var(--accent-red)'
            if (node.data?.status === 'complete') return 'var(--accent-green)'
            if (node.data?.status === 'running') return 'var(--accent-blue)'
            return nodeDef?.color || 'var(--bg-tertiary)'
          }}
          maskColor="rgba(0, 0, 0, 0.8)"
        />
        <Background variant="dots" gap={20} size={1} color="var(--border-color)" />
      </ReactFlow>

      {nodes.length === 0 && (
        <div className="canvas-empty-state">
          <div className="empty-icon">
            <Workflow size={48} />
          </div>
          <h3>Design Your Workflow</h3>
          <p>Drag nodes from the palette to get started</p>
        </div>
      )}
    </div>
  )
}

export default DesignerWorkflowCanvas
